/**
 * PlaybackControls Component
 * Provides transport controls for playback (play, pause, stop, loop)
 * and composition controls (clear, reset, export, share)
 */
import { useState, useCallback, useEffect } from 'react';
/**
 * Props for PlaybackControls component
 * Updated to include onLoad callback for loading saved compositions
 */
export interface PlaybackControlsProps {
    /** Whether audio is currently playing */
    isPlaying: boolean;
    /** Whether audio is paused */
    isPaused: boolean;
    /** Whether loop mode is enabled */
    loopEnabled: boolean;
    /** Whether the grid has any active notes */
    hasNotes: boolean;
    /** Number of steps in the composition */
    steps: number;
    /** Beats per minute (tempo) */
    bpm: number;
    /** Play callback */
    onPlay: () => Promise<void>;
    /** Pause callback */
    onPause: () => void;
    /** Stop callback */
    onStop: () => void;
    /** Toggle loop callback */
    onToggleLoop: () => void;
    /** Clear grid callback */
    onClear: () => void;
    /** Reset to fresh slate callback */
    onReset: () => void;
    /** Export audio callback */
    onExport: () => Promise<void>;
    /** Share composition callback */
    onShare: () => Promise<void>;
    /** Steps change callback */
    onStepsChange: (steps: number) => void;
    /** BPM change callback */
    onBpmChange: (bpm: number) => void;
    /** Help button callback */
    onHelp?: () => void;
    /** Save composition callback */
    onSave?: () => void;
    /** Load composition callback */
    onLoad?: () => void;
    /** Whether user is authenticated */
    isAuthenticated?: boolean;
    /** Whether this is an existing composition being edited */
    isExistingComposition?: boolean;
}
/**
 * PlaybackControls component
 * Renders all playback and composition control buttons
 */
export function PlaybackControls({ isPlaying, isPaused, loopEnabled, hasNotes, steps, bpm, onPlay, onPause, onStop, onToggleLoop, onClear, onReset, onExport, onShare, onStepsChange, onBpmChange, onHelp, onSave, onLoad, isAuthenticated = false, isExistingComposition = false, }: PlaybackControlsProps) {
    const [isExporting, setIsExporting] = useState(false);
    const [isSharing, setIsSharing] = useState(false);
    const [stepsInput, setStepsInput] = useState(steps.toString());
    const [bpmInput, setBpmInput] = useState(bpm.toString());
    const [isStepsEditing, setIsStepsEditing] = useState(false);
    const [isBpmEditing, setIsBpmEditing] = useState(false);
    // Sync local state with props only when not editing
    useEffect(() => {
        if (!isStepsEditing) {
            setStepsInput(steps.toString());
        }
    }, [steps, isStepsEditing]);
    useEffect(() => {
        if (!isBpmEditing) {
            setBpmInput(bpm.toString());
        }
    }, [bpm, isBpmEditing]);
    // Handle keyboard shortcut for save (Ctrl+S / Cmd+S)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                if (onSave && isAuthenticated && hasNotes) {
                    onSave();
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onSave, isAuthenticated, hasNotes]);
    /**
     * Handle play button click
     */
    const handlePlay = useCallback(async () => {
        try {
            await onPlay();
        }
        catch (error) {
            console.error('[PlaybackControls] Play failed', error);
            alert('Failed to start playback: ' + error);
        }
    }, [onPlay]);
    /**
     * Handle export button click
     */
    const handleExport = useCallback(async () => {
        setIsExporting(true);
        try {
            await onExport();
        }
        catch (error) {
            console.error('[PlaybackControls] Export failed', error);
        }
        finally {
            setIsExporting(false);
        }
    }, [onExport]);
    /**
     * Handle share button click
     */
    const handleShare = useCallback(async () => {
        setIsSharing(true);
        try {
            await onShare();
        }
        catch (error) {
            console.error('[PlaybackControls] Share failed', error);
        }
        finally {
            setIsSharing(false);
        }
    }, [onShare]);
    /**
     * Handle steps input change
     */
    const handleStepsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        setStepsInput(inputValue);
        setIsStepsEditing(true);
        const value = parseInt(inputValue, 10);
        if (!isNaN(value) && value > 0) {
            onStepsChange(value);
        }
    }, [onStepsChange]);
    const handleStepsBlur = useCallback(() => {
        setIsStepsEditing(false);
        // Ensure input shows valid value on blur
        setStepsInput(steps.toString());
    }, [steps]);
    /**
     * Handle BPM input change
     */
    const handleBpmChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        setBpmInput(inputValue);
        setIsBpmEditing(true);
        const value = parseInt(inputValue, 10);
        if (!isNaN(value) && value > 0 && value <= 300) {
            onBpmChange(value);
        }
    }, [onBpmChange]);
    const handleBpmBlur = useCallback(() => {
        setIsBpmEditing(false);
        // Ensure input shows valid value on blur
        setBpmInput(bpm.toString());
    }, [bpm]);
    return (<section id="controls-section" aria-label="Music composition controls" style={{ marginBottom: '1rem', marginTop: '1.5rem' }}>
      <h2 className="sr-only">Playback and Composition Controls</h2>
      <div id="controls" role="toolbar" aria-label="Music controls" style={{
            width: '91.666667%',
            maxWidth: '1400px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(4rem, 1fr))',
            gap: '4px',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'var(--color-surface)',
            padding: '0.5rem'
        }}>
        {/* Play Button */}
        <button id="play" onClick={() => {
            handlePlay();
        }} disabled={!hasNotes || (isPlaying && !isPaused)} title={hasNotes ? "Play composition" : "Add notes to the grid first"} aria-label="Play" className="disabled:opacity-50 disabled:cursor-not-allowed">
          <i className="fa-solid fa-play"></i>
        </button>

        {/* Stop Button */}
        <button id="stop" onClick={onStop} disabled={!isPlaying && !isPaused} title="Stop playback" aria-label="Stop" className="disabled:opacity-50 disabled:cursor-not-allowed">
          <i className="fa-solid fa-stop"></i>
        </button>

        {/* Pause Button */}
        <button id="pause" onClick={onPause} disabled={!isPlaying || isPaused} title="Pause playback" aria-label="Pause" className="disabled:opacity-50 disabled:cursor-not-allowed">
          <i className="fa-solid fa-pause"></i>
        </button>

        {/* Loop Button */}
        <button id="loop-btn" onClick={onToggleLoop} title="Toggle loop mode" aria-label="Toggle Loop" className={loopEnabled ? 'active' : ''}>
          <i className="fas fa-sync-alt"></i>
        </button>

        {/* Clear Button */}
        <button id="clear" onClick={onClear} title="Clear all notes" aria-label="Clear Notes">
          <i className="fa-solid fa-broom"></i>
        </button>

        {/* Reset Button */}
        <button id="reset-btn" onClick={onReset} title="Reset to fresh slate" aria-label="Fresh Slate">
          <i className="fa-solid fa-rotate-right"></i>
        </button>

        {/* Export Button */}
        <button id="export" onClick={handleExport} disabled={isExporting} title="Export composition to WAV file (1 credit)" aria-label="Export to WAV" className="disabled:opacity-50 disabled:cursor-not-allowed">
          {isExporting ? (<i className="fa-solid fa-spinner fa-spin"></i>) : (<i className="fas fa-file-export"></i>)}
        </button>

        {/* Share Button */}
        <button id="share-btn" onClick={handleShare} disabled={isSharing} title="Share your composition" aria-label="Share Composition" className="disabled:opacity-50 disabled:cursor-not-allowed">
          {isSharing ? (<i className="fa-solid fa-spinner fa-spin"></i>) : (<i className="fas fa-share-alt"></i>)}
        </button>

        {/* Save Button */}
        {onSave && (<div style={{ position: 'relative', display: 'inline-block' }} title={!isAuthenticated
                ? 'Login to save compositions'
                : !hasNotes
                    ? 'Add notes to save'
                    : isExistingComposition
                        ? 'Update composition (Ctrl+S)'
                        : 'Save composition (Ctrl+S)'}>
            <button id="save-btn" onClick={onSave} disabled={!isAuthenticated || !hasNotes} aria-label={isExistingComposition ? 'Update Composition' : 'Save Composition'} className="disabled:opacity-50 disabled:cursor-not-allowed" style={{ pointerEvents: (!isAuthenticated || !hasNotes) ? 'none' : 'auto' }}>
              <i className={isExistingComposition ? 'fas fa-save' : 'fas fa-floppy-disk'}></i>
            </button>
          </div>)}

        {/* Load Button */}
        {onLoad && (<div style={{ position: 'relative', display: 'inline-block' }} title={!isAuthenticated
                ? 'Login to load compositions'
                : 'Load saved compositions'}>
            <button id="load-btn" onClick={onLoad} disabled={!isAuthenticated} aria-label="Load Composition" className="disabled:opacity-50 disabled:cursor-not-allowed" style={{ pointerEvents: !isAuthenticated ? 'none' : 'auto' }}>
              <i className="fas fa-folder-open"></i>
            </button>
          </div>)}

        {/* Help Button */}
        {onHelp && (<button id="help-btn" onClick={onHelp} title="Show help information" aria-label="Help">
            ?
          </button>)}

        {/* Steps Input */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
          <label htmlFor="steps-input" style={{ fontSize: '0.65rem', color: 'var(--color-fg)' }}>
            Steps
          </label>
          <input id="steps-input" type="number" min="1" value={stepsInput} onChange={handleStepsChange} onBlur={handleStepsBlur} onFocus={() => setIsStepsEditing(true)} title="Number of steps per loop" aria-label="Steps per loop" style={{
            width: '100%',
            background: 'var(--color-surface)',
            border: '4px solid var(--color-cell-border)',
            color: 'var(--color-fg)',
            textAlign: 'center',
            borderRadius: '4px',
            fontFamily: 'var(--font-family)',
            padding: '0.25rem'
        }}/>
        </div>

        {/* BPM Input */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
          <label htmlFor="bpm-input" style={{ fontSize: '0.65rem', color: 'var(--color-fg)' }}>
            BPM
          </label>
          <input id="bpm-input" type="number" min="1" max="300" value={bpmInput} onChange={handleBpmChange} onBlur={handleBpmBlur} onFocus={() => setIsBpmEditing(true)} title="Beats per minute (tempo)" aria-label="Beats per minute" style={{
            width: '100%',
            background: 'var(--color-surface)',
            border: '4px solid var(--color-cell-border)',
            color: 'var(--color-fg)',
            textAlign: 'center',
            borderRadius: '4px',
            fontFamily: 'var(--font-family)',
            padding: '0.25rem'
        }}/>
        </div>
      </div>
    </section>);
}
